import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import Text from '../components/shared/Text';
import { useToast } from '../components/shared/ToastNotification';

interface Product {
    id: number;
    name: string;
    sku: string;
    category_name: string;
    stock_quantity: number;
    unit: string;
    min_stock: number;
    track_batches: boolean;
}

interface Vendor {
    id: number;
    name: string;
}

function RestockForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const toast = useToast();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        product_id: '',
        product_variant_id: '',
        quantity: '',
        cost: '',
        restock_date: new Date().toISOString().split('T')[0],
        vendor_id: '',
        notes: '',
        batch_number: '',
        expiry_date: '',
        manufacturing_date: '',
        mrp: '',
        track_batches: false,
        tax_rate_id: ''
    });
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [currentStock, setCurrentStock] = useState<number | null>(null);
    const [recentRestocks, setRecentRestocks] = useState<any[]>([]);
    const [loadingRecent, setLoadingRecent] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Search State
    const [productSearch, setProductSearch] = useState('');
    const [searchResults, setSearchResults] = useState<Product[]>([]);
    const [showResults, setShowResults] = useState(false);
    const [variants, setVariants] = useState<any[]>([]);
    const [productBatches, setProductBatches] = useState<any[]>([]);
    const [taxRates, setTaxRates] = useState<any[]>([]);

    useEffect(() => {
        loadVendors();
        loadRecentRestocks();
        fetchTaxRates();
    }, []);

    const loadRecentRestocks = async () => {
        try {
            setLoadingRecent(true);
            const response = await api.get('/restocks', { per_page: 5 });
            setRecentRestocks(response.data || []);
        } catch (err) {
            console.error('Failed to load recent restocks', err);
        } finally {
            setLoadingRecent(false);
        }
    };

    const handleProductSearch = async (e: any) => {
        const query = e.target.value;
        setProductSearch(query);

        if (query.length < 2) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        try {
            const response = await api.get(`/products?q=${encodeURIComponent(query)}`);
            const products = response.data?.products || response.data || [];
            setSearchResults(products);
            setShowResults(true);
        } catch (error) {
            console.error('Search failed:', error);
        }
    };

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const loadVendors = async () => {
        try {
            const response = await api.get('/vendors');
            setVendors(response.data || []);
        } catch (err) {
            console.error('Failed to load vendors', err);
        }
    };

    const selectProduct = async (product: Product) => {
        setSelectedProduct(product);
        setFormData(prev => ({
            ...prev,
            product_id: product.id.toString(),
            track_batches: product.track_batches,
            product_variant_id: ''
        }));
        setProductSearch(product.name);
        setShowResults(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const payload = {
                purchase_date: formData.restock_date,
                status: 'received',
                notes: formData.notes,
                vendor_id: formData.vendor_id || null,
                purchase_items_attributes: [
                    {
                        product_id: formData.product_id,
                        quantity: formData.quantity,
                        unit_price: formData.cost,
                        tax_rate_id: formData.tax_rate_id,
                        discount_percent: 0,
                        unit: selectedProduct?.unit || 'piece',
                        batch_number: formData.track_batches ? formData.batch_number : null,
                        expiry_date: formData.track_batches ? formData.expiry_date : null,
                        manufacturing_date: formData.track_batches ? formData.manufacturing_date : null,
                        mrp: formData.mrp,
                        product_variant_id: formData.product_variant_id
                    }
                ]
            };

            const response = isEdit
                ? await api.put(`/restocks/${id}`, { restock: payload })
                : await api.post('/restocks', { restock: payload });

            if (response.success) {
                toast.success(`Stock ${isEdit ? 'updated' : 'added'} successfully!`);
                setShowSuccess(true);
                loadRecentRestocks();
                if (!isEdit) {
                    // Reset form but keep vendor if adding more
                    setFormData(prev => ({
                        ...prev,
                        product_id: '',
                        product_variant_id: '',
                        quantity: '',
                        cost: '',
                        track_batches: false,
                        tax_rate_id: ''
                    }));
                    setSelectedProduct(null);
                    setProductSearch('');
                    setVariants([]);
                    setCurrentStock(null);
                }
            } else {
                const errorMsg = response.errors
                    ? (Array.isArray(response.errors) ? response.errors[0] : JSON.stringify(response.errors))
                    : (response.message || response.error || 'Failed to save restock');
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const quantity = (Number(formData.quantity) || 0);
    const cost = (Number(formData.cost) || 0);
    const subtotal = quantity * cost;
    const selectedTaxRate = taxRates.find(tr => String(tr.id) === String(formData.tax_rate_id));
    const taxPercentage = selectedTaxRate?.percentage || 0;
    const taxAmount = subtotal * (taxPercentage / 100);
    const totalCost = subtotal + taxAmount;

    // @ts-ignore
    const firmState = (window as any).CURRENT_FIRM?.state || '';
    const selectedVendor = vendors.find(v => v.id === Number(formData.vendor_id));
    const vendorState = (selectedVendor as any)?.state || '';
    const isInterState = firmState && vendorState && firmState.toLowerCase() !== vendorState.toLowerCase();

    if (showSuccess) {
        return (
            <div className="max-w-2xl mx-auto mt-20 text-center animate-in fade-in zoom-in duration-300">
                <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-100/50">
                    <svg className="w-12 h-12 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 className="text-3xl font-bold text-slate-800 mb-4">Stock Added Successfully!</h1>
                <p className="text-slate-500 mb-10 text-lg">Inventory levels for <b>{productSearch}</b> have been updated.</p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                        onClick={() => setShowSuccess(false)}
                        className="w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg"
                    >
                        Add More Stock
                    </button>
                    <Link
                        to="/restocks"
                        className="w-full sm:w-auto px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50 transition-all"
                    >
                        View History
                    </Link>
                    <Link
                        to="/products"
                        className="w-full sm:w-auto px-8 py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-all"
                    >
                        Go to Products
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8 flex justify-between items-end">
                <div>
                    <div className="flex items-center gap-4">
                        <Link to="/restocks" className="w-10 h-10 bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-lg rounded-xl flex items-center justify-center transition-all duration-300">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>
                        <h1 className="text-4xl font-black text-slate-900 tracking-tight">{isEdit ? 'Refine' : 'Inject'} <span className="text-indigo-600">Stock</span></h1>
                    </div>
                    <p className="text-slate-500 mt-2 ml-14 text-sm font-medium tracking-wide uppercase opacity-70 italic">{isEdit ? 'Update existing registry entry' : 'Digital synchronization with physical inventory'}</p>
                </div>
                <div className="hidden lg:block text-right">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Session Summary</div>
                    <div className="text-sm font-medium text-slate-600">Managing stock levels across {vendors.length} vendors</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Main Form Section */}
                <div className="lg:col-span-8">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                        <div className="p-8">
                            <form onSubmit={handleSubmit} className="space-y-8">
                                {error && (
                                    <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-xl flex items-center gap-3">
                                        <svg className="w-5 h-5 text-rose-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-rose-700 font-medium">{error}</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        {/* Product Selection */}
                                        <div className="relative group">
                                            <div className="flex items-center gap-2 mb-2 ml-1">
                                                <div className="w-4 h-4 bg-indigo-600 rounded-md"></div>
                                                <label className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">Product Identification</label>
                                            </div>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-indigo-500 transition-colors">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Search SKU, Name or Category..."
                                                    value={productSearch}
                                                    onChange={handleProductSearch}
                                                    onFocus={() => { if (productSearch) setShowResults(true); }}
                                                    className="w-full pl-12 pr-6 py-4 rounded-2xl bg-white border border-slate-100 shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none font-black text-slate-800"
                                                />
                                                {showResults && (
                                                    <div className="absolute top-full left-0 w-full mt-3 bg-white border border-slate-100 rounded-3xl shadow-2xl z-50 max-h-80 overflow-y-auto p-3 scrollbar-hide ring-1 ring-slate-100 animate-in fade-in slide-in-from-top-2">
                                                        {searchResults.length > 0 ? (
                                                            searchResults.map((product: any) => (
                                                                <div
                                                                    key={product.id}
                                                                    onClick={() => selectProduct(product)}
                                                                    className="p-4 hover:bg-indigo-50/50 rounded-2xl cursor-pointer transition-all group/item flex items-center justify-between"
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="font-black text-slate-800 group-hover/item:text-indigo-600 transition-colors truncate">{product.name}</div>
                                                                        <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2 font-bold uppercase tracking-widest">
                                                                            <span>{product.category_name}</span>
                                                                            <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                                                                            <span className="text-indigo-400">SKU: {product.sku || '---'}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className={`ml-4 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter ${product.stock_quantity <= product.min_stock ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                                                        {product.stock_quantity} {product.unit}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="px-4 py-8 text-center">
                                                                <div className="text-3xl mb-2 opacity-20">🔍</div>
                                                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No Matches Found</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Vendor Selection */}
                                        <div className="group">
                                            <div className="flex items-center gap-2 mb-2 ml-1">
                                                <div className="w-4 h-4 bg-slate-400 rounded-md"></div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Source Vendor</label>
                                            </div>
                                            <div className="relative">
                                                <select
                                                    name="vendor_id"
                                                    value={formData.vendor_id}
                                                    onChange={handleChange}
                                                    className="w-full pl-6 pr-12 py-4 rounded-2xl bg-slate-50 border border-slate-100 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-300 appearance-none outline-none font-black text-slate-700 transition-all cursor-pointer hover:bg-white"
                                                >
                                                    <option value="">Direct Entry (No Vendor)</option>
                                                    {vendors.map((vendor: any) => (
                                                        <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                                                    ))}
                                                </select>
                                                <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                                </div>
                                            </div>
                                        </div>

                                        {variants.length > 0 && (
                                            <div className="animate-in slide-in-from-top-4 duration-300">
                                                <label className="block text-sm font-bold text-slate-700 mb-2.5">Product Variant <span className="text-rose-500">*</span></label>
                                                <select
                                                    name="product_variant_id"
                                                    value={formData.product_variant_id}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3.5 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                                >
                                                    <option value="">Select specific variation...</option>
                                                    {variants.map((v: any) => (
                                                        <option key={v.id} value={v.id}>
                                                            {v.options.map((o: any) => o.presentation || o.name).join(' / ')} (SKU: {v.sku})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-6">
                                        {/* Current Stock Snapshot */}
                                        <div className={`p-6 rounded-3xl border-2 transition-all duration-500 ${selectedProduct ? 'opacity-100 scale-100' : 'opacity-30 scale-95 grayscale'}`}>
                                            {!selectedProduct ? (
                                                <div className="h-full flex flex-col items-center justify-center text-center py-6">
                                                    <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                                                        <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4M4 7c0-2.21 3.58-4 8-4s8 1.79 8 4" />
                                                        </svg>
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-400">Select a product to see stats</span>
                                                </div>
                                            ) : (
                                                <div className={`h-full ${currentStock !== null && currentStock > 50
                                                    ? 'bg-emerald-50/50 border-emerald-100'
                                                    : currentStock !== null && currentStock > 10
                                                        ? 'bg-amber-50/50 border-amber-100'
                                                        : 'bg-rose-50/50 border-rose-100'
                                                    }`}>
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div>
                                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Current Snapshot</div>
                                                            <div className="text-4xl font-black text-slate-800 tracking-tighter mt-1">{currentStock} <span className="text-lg font-medium text-slate-400 tracking-normal">{selectedProduct.unit || 'pcs'}</span></div>
                                                        </div>
                                                        <div className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${currentStock !== null && currentStock > 20 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                            {currentStock !== null && currentStock > 20 ? 'Active' : 'Low'}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-slate-500 leading-relaxed">
                                                        {currentStock !== null && currentStock > 50 ? 'Stock levels are optimal. Adding more will increase your inventory buffer.' : 'Critical level. Restocking immediately is highly recommended.'}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Date Field */}
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2.5">Stock Entry Date</label>
                                            <input
                                                type="date"
                                                name="restock_date"
                                                value={formData.restock_date}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3.5 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Batch Details Section - Conditionally Shown */}
                                {formData.track_batches && (
                                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-3xl p-8 animate-in slide-in-from-bottom-4 duration-500">
                                        <div className="flex items-center gap-2 mb-6">
                                            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-widest">Enhanced Tracking: Batch Details</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                            <div className="md:col-span-2">
                                                <label className="block text-[11px] font-bold text-indigo-600 uppercase mb-2 ml-1">Batch Number <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="text"
                                                    name="batch_number"
                                                    value={formData.batch_number}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                                                    placeholder="e.g. BATCH-A1"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-[11px] font-bold text-indigo-600 uppercase mb-2 ml-1">Batch MRP (₹)</label>
                                                <input
                                                    type="number"
                                                    name="mrp"
                                                    value={formData.mrp}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-[11px] font-bold text-indigo-600 uppercase mb-2 ml-1">Mfg. Date</label>
                                                <input
                                                    type="date"
                                                    name="manufacturing_date"
                                                    value={formData.manufacturing_date}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-[11px] font-bold text-indigo-600 uppercase mb-2 ml-1">Expiry Date <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="date"
                                                    name="expiry_date"
                                                    value={formData.expiry_date}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 rounded-xl border border-indigo-200 focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Quantity and Cost - The Core Calculation */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end p-8 bg-slate-50 rounded-3xl border border-slate-100">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2.5">Refill Quantity <span className="text-rose-500">*</span></label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                name="quantity"
                                                value={formData.quantity}
                                                onChange={handleChange}
                                                required
                                                min="0.01"
                                                step="0.01"
                                                placeholder="0.00"
                                                className="w-full px-4 py-4 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-emerald-500 transition-all outline-none text-xl font-bold text-slate-800"
                                            />
                                            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400 font-medium">
                                                {selectedProduct?.unit || 'Units'}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2.5">Buy Price (₹)</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 font-bold">
                                                ₹
                                            </div>
                                            <input
                                                type="number"
                                                name="cost"
                                                value={formData.cost}
                                                onChange={handleChange}
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                className="w-full pl-8 pr-4 py-4 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none text-xl font-bold text-slate-800"
                                            />
                                        </div>
                                    </div>

                                    {!formData.track_batches && (
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2.5">MRP (₹)</label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-emerald-400 font-bold">
                                                    ₹
                                                </div>
                                                <input
                                                    type="number"
                                                    name="mrp"
                                                    value={formData.mrp}
                                                    onChange={handleChange}
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    className="w-full pl-8 pr-4 py-4 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 transition-all outline-none text-xl font-bold text-emerald-700"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <label className="block text-sm font-bold text-slate-700 mb-2.5">Tax Rate</label>
                                        <div className="relative">
                                            <select
                                                name="tax_rate_id"
                                                value={formData.tax_rate_id}
                                                onChange={handleChange}
                                                className="w-full pl-4 pr-10 py-4 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-emerald-500 transition-all outline-none text-xl font-bold text-slate-800 appearance-none bg-white"
                                            >
                                                <option value="">Select Tax Rate</option>
                                                {taxRates.map(tr => (
                                                    <option key={tr.id} value={tr.id}>
                                                        {tr.name} ({tr.percentage}%)
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400 font-bold">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md:col-span-2 pt-6 border-t border-slate-200/60 px-2 flex flex-col gap-5">
                                        <div className="flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-1">
                                            <span>Subtotal Analysis</span>
                                            <span className="text-slate-600">₹{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                        
                                        <div className="bg-white/50 rounded-2xl p-4 border border-slate-100 flex flex-col gap-3">
                                            {isInterState ? (
                                                <div className="flex justify-between items-center text-[10px] font-black text-emerald-500 uppercase tracking-widest">
                                                    <span className="flex items-center gap-1.5">
                                                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></div>
                                                        Integrated Tax (IGST {taxPercentage}%)
                                                    </span>
                                                    <span>+ ₹{taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex justify-between items-center text-[10px] font-black text-emerald-500 uppercase tracking-widest">
                                                        <span className="flex items-center gap-1.5">
                                                            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></div>
                                                            Central Tax (CGST {taxPercentage / 2}%)
                                                        </span>
                                                        <span>+ ₹{(taxAmount / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-[10px] font-black text-emerald-500 uppercase tracking-widest">
                                                        <span className="flex items-center gap-1.5">
                                                            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></div>
                                                            State Tax (SGST {taxPercentage / 2}%)
                                                        </span>
                                                        <span>+ ₹{(taxAmount / 2).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        <div className="flex justify-between items-center mt-2 p-6 bg-indigo-600 rounded-3xl shadow-xl shadow-indigo-100 text-white group-hover:bg-indigo-700 transition-colors">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60">Total Investment Value</span>
                                                <span className="text-4xl font-black font-mono tracking-tighter">₹{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/20">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2.5">Additional Documentation / Notes</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={3}
                                        placeholder="Add any specific details about this batch or delivery context..."
                                        className="w-full px-4 py-4 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none resize-none"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-4 pt-4">
                                    <Link
                                        to="/restocks"
                                        className="px-8 py-4 bg-white border-2 border-slate-100 rounded-2xl text-sm font-bold text-slate-600 hover:bg-slate-50 hover:border-slate-200 transition-all"
                                    >
                                        Discard
                                    </Link>
                                    <button
                                        type="submit"
                                        disabled={saving || !selectedProduct}
                                        className={`px-12 py-4 bg-indigo-600 text-white rounded-2xl text-sm font-bold hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center gap-3 ${saving || !selectedProduct ? 'opacity-50 cursor-not-allowed grayscale' : 'hover:-translate-y-1'}`}
                                    >
                                        {saving ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {isEdit ? 'Updating Storage...' : 'Injecting Stock...'}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                                {isEdit ? 'Save Changes' : 'Confirm Restock'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Sidebar Section: Recent History */}
                <div className="lg:col-span-4 space-y-6">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/50 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-slate-800 tracking-tight">Recent Activity</h2>
                            <Link to="/restocks" className="text-xs font-bold text-indigo-600 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-all">View All</Link>
                        </div>

                        {loadingRecent ? (
                            <div className="space-y-4 py-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="animate-pulse flex items-center gap-4">
                                        <div className="w-10 h-10 bg-slate-100 rounded-xl"></div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 bg-slate-100 rounded w-2/3"></div>
                                            <div className="h-2 bg-slate-50 rounded w-1/3"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : recentRestocks.length > 0 ? (
                            <div className="space-y-5">
                                {recentRestocks.map((restock: any) => (
                                    <div key={restock.id} className="group cursor-pointer">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 bg-slate-100 group-hover:bg-indigo-50 text-slate-400 group-hover:text-indigo-600 rounded-xl flex items-center justify-center transition-all shrink-0">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold text-slate-800 line-clamp-1 group-hover:text-indigo-600 transition-colors">
                                                    {restock.items?.[0]?.product?.name || `Entry ${restock.purchase_number || 'N/A'}`}
                                                </div>
                                                <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                                                    <span>{new Date(restock.purchase_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                    <span>•</span>
                                                    <span className="font-bold text-emerald-600">₹{restock.total_amount?.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <p className="text-sm text-slate-400">No recent restocks found.</p>
                            </div>
                        )}
                    </div>

                    {/* Pro Tips Section */}
                    <div className="bg-indigo-900 rounded-3xl p-6 text-indigo-50 shadow-2xl shadow-indigo-200/50">
                        <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center mb-4">
                            <svg className="w-5 h-5 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-sm font-bold mb-2">Pro Tip: Cost Management</h3>
                        <p className="text-xs text-indigo-200/80 leading-relaxed mb-4">
                            Adding accurate unit costs helps you track profit margins automatically in your sales reports.
                        </p>
                        <ul className="text-[10px] font-bold space-y-2 uppercase tracking-wider text-indigo-400">
                            <li className="flex items-center gap-2 italic">✓ Supports FIFO Valuations</li>
                            <li className="flex items-center gap-2 italic">✓ Auto-update Avg Cost</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RestockForm;
